import hslToHex from 'hsl-to-hex';
import { CHART_GROUPS } from '@/components/shared/visnet-hubs-common-consts';

const COLORS = {
  REDS: [
    '#990000',
    '#ff0000',
    '#ff1a1a',
    '#ff3333',
    '#ff4d4d',
    '#ff6666',
    '#ff8080',
    '#ff9999',
    '#b30000'
  ],
  BLUES: [
    '#0000ff',
    '#0000e6',
    '#0000cc',
    '#0000b3',
    '#000099',
    '#000080',
    '#000066',
    '#9999ff',
    '#b3b3ff',
    '#ccccff'
  ],
  BLACKS: [
    '#000000',
    '#1a1a1a',
    '#333333',
    '#4d4d4d',
    '#666666',
    '#808080',
    '#999999',
    '#b3b3b3',
    '#cccccc'
  ],
  YELLOWS: [
    '#ffff00',
    '#e6e600',
    '#cccc00',
    '#b3b300',
    '#ffff4d',
    '#ffff66',
    '#ffff80',
    '#ffff99',
    '#ffffe6'
  ],
  GREENS: [
    '#00ff00',
    '#00e600',
    '#00cc00',
    '#00b300',
    '#009900',
    '#008000',
    '#006600',
    '#99ff99',
    '#b3ffb3'
  ]
};

const CURRENTS_MAP = {
  0: COLORS.GREENS,
  1: COLORS.REDS,
  2: COLORS.YELLOWS,
  3: COLORS.BLUES
};

const electrical = [{ label: 'current', value: CURRENTS_MAP }];

const environmental = [
  { label: 'outdoorTemperature', value: COLORS.GREENS[0] },
  { label: 'indoorTemperature', value: COLORS.YELLOWS[0] },
  { label: 'indoorHumidity', value: COLORS.REDS[0] },
  { label: 'txTemperature', value: COLORS.BLUES[0] }
];
const predefinedGroup = { electrical, environmental };

const GOLDEN_RATIO_CONJUGATE = 0.618033988749895;
const H_SPECTRUM = 360;

/**
 * This is a method that is based on golden ratio color theory.
 * Adding @GOLDEN_RATIO_CONJUGATE creates smooth intervals between near series color
 *  and doing modulo 3.6 allows to  have H value in 360 range
 * @return {string} Return color in hex format
 * */
const goldenRatioAlgorithm = ({ currentIndex, startHueValue, totalSize }) => {
  const valueTimesInterval = (H_SPECTRUM / totalSize) * currentIndex * 0.01;
  const hValue = ((valueTimesInterval + startHueValue + GOLDEN_RATIO_CONJUGATE) % 3.6) * 100;

  return hslToHex(hValue, 100, 50);
};
/**
 * This is a method for color generation based on attribute name.
 * Spectrum/Map.size defines interval at which spectrum of H (0-360)
 * is being divided. Each attribute will then have its own start.
 * Then we iterate through attribute subArray and use goldenRatio algorithm
 * to create colors for series.
 *
 * Disclaimer - we're mutating attributeArray
 *
 * @param {Map} mapObject Map object with attributes as keys. Keys have arrays
 * of highcharts series as values.
 * */
export const generateColorForNonMapped = mapObject => {
  let mapIndex = 0;

  mapObject.forEach((attributeArray, _, initialMap) => {
    const startHueValue = (H_SPECTRUM / initialMap.size) * mapIndex * 0.01;

    attributeArray.forEach((seriesObject, mapSubArrayIndex, mapSubArray) => {
      if (seriesObject.color) return false;

      const generateColorForValue = goldenRatioAlgorithm({
        currentIndex: mapSubArrayIndex,
        startHueValue,
        totalSize: mapSubArray.length
      });

      // eslint-disable-next-line
      attributeArray[mapSubArrayIndex] = {
        ...attributeArray[mapSubArrayIndex],
        color: generateColorForValue
      };
      return true;
    });
    mapIndex += 1;
  });
  return mapObject;
};

export const generateColorForNonMappedArray = (mapArray = []) => {
  return mapArray.map((series, index, initialArray) => {
    const startHueValue = (H_SPECTRUM / initialArray.length) * index * 0.01;

    const generateColorForValue = goldenRatioAlgorithm({
      currentIndex: index,
      startHueValue,
      totalSize: initialArray.length
    });

    return {
      ...series,
      color: generateColorForValue
    };
  });
};

export const generateColorForMappedArray = (mapArray = []) => {
  return mapArray.map((series, index, initialArray) => {
    const { group, phaseNumber, feederNumber } = series;

    if (group === 'Current') {
      return {
        ...series,
        color: CURRENTS_MAP[phaseNumber][feederNumber - 1]
      };
    }
    const startHueValue = (H_SPECTRUM / initialArray.length) * index * 0.01;

    const generateColorForValue = goldenRatioAlgorithm({
      currentIndex: index,
      startHueValue,
      totalSize: initialArray.length
    });

    return {
      ...series,
      color: generateColorForValue
    };
  });
};

export const generatePredefinedColor = ({
  chartGroupType,
  electricalValues = {},
  attributeName
}) => {
  const { phase, feeder = null } = electricalValues;
  const foundGroup = predefinedGroup[chartGroupType].find(el => el.label === attributeName);

  if (!foundGroup) {
    return false;
  }
  if (chartGroupType === CHART_GROUPS.ELECTRICAL) {
    const feederRefValue = feeder === null ? 0 : feeder + 1;

    return foundGroup.value[Number(phase)][Number(feederRefValue)];
  }
  return foundGroup.value;
};
